import type { AiModelRecord } from "./types.js";

/** OpenRouter `supported_parameters` values that indicate reasoning API support. */
export const REASONING_SUPPORTED_PARAMETERS = ["reasoning", "include_reasoning"] as const;

export type ReasoningModelInput = Pick<
  AiModelRecord,
  "supportedParameters" | "pricing" | "openRouterPricing"
> & {
  supportsReasoning?: boolean;
};

/**
 * Whether the model accepts OpenRouter's `reasoning` request parameter
 * (effort, max_tokens, exclude, enabled).
 */
export function supportsReasoningParameter(model: Pick<AiModelRecord, "supportedParameters">): boolean {
  return model.supportedParameters.some((p) =>
    (REASONING_SUPPORTED_PARAMETERS as readonly string[]).includes(p),
  );
}

/**
 * Whether the model bills separate reasoning / thinking tokens
 * (`internal_reasoning` in OpenRouter pricing).
 */
export function hasReasoningPricing(
  model: Pick<AiModelRecord, "pricing" | "openRouterPricing">,
): boolean {
  const raw = model.openRouterPricing?.internal_reasoning;
  if (raw !== undefined && raw !== null && String(raw).trim() !== "") {
    return true;
  }
  return model.pricing.reasoningUsdPerToken !== undefined;
}

/**
 * Compute `supportsReasoning` from catalog fields (used when normalizing catalog entries).
 */
export function computeSupportsReasoning(model: ReasoningModelInput): boolean {
  return supportsReasoningParameter(model) || hasReasoningPricing(model);
}

/**
 * True when the model supports reasoning tokens (API param and/or separate reasoning pricing).
 *
 * Prefer `model.supportsReasoning` on {@link AiModelRecord} after catalog load;
 * this helper also works when only partial fields are available.
 */
export function isReasoningModel(model: ReasoningModelInput): boolean {
  if (model.supportsReasoning !== undefined) {
    return model.supportsReasoning;
  }
  return computeSupportsReasoning(model);
}
